import React, { useState, useRef } from "react";
import Footer from "../components/Footer/Footer.js";
import FixedPlugin from "../components/FixedPlugin/FixedPlugin.js";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from '@mui/styles';
import { Button } from 'reactstrap';
import CoreSpinner from '../components/_core/CoreSpinner';
import PBCSDark from "../assets/img/pbcs_dark.png";
import PBCSLight from "../assets/img/pbcs_light.png";
import LogoDark from "../assets/img/SDP_dark.png";
import { ThemeContext } from '../contexts/ThemeContext';
import { useAuth } from "../contexts/AuthContext";
import { roles } from 'actions/auth';
import { getUser } from 'actions/helpers';
import TextBox from '../components/_core/Inputs/TextBox';


const useStyles = makeStyles((theme) => ({
  box: {
    marginTop: "10%",
    margin: "auto",
    width: "50%",
    padding: 10
  },
  Wrapper: {
    position: "relative",
    background: 'linear-gradient(to bottom right, #4a5a8a, #8ba4d9, #c5d5f0)'
  },
  logo: {
    marginTop: '30px',
    marginBottom: '10px',
    marginLeft: '40px',
    margin: "3%",
    width: "140px"
  },
  Text_SDP: {
    position: 'absolute',
    width: '30px',
    height: '16px',
    left: '183px',
    top: '38px',
    fontWeight: '500',
    fontSize: '25px',
    color: '#FFFFFF'
  },
  PBCS: {
    width: '304px',
    height: '30px',
    float: 'right',
    marginRight: '40px',
    marginTop: '20px',
    fontStyle: 'normal',
    fontWeightt: '400',
    fontSize: '25px',
    color: '#2D76BC'
  },
  LoginButton: {
    padding: "14px 46px 14px 46px",
    fontSize: "15px!important",
    marginTop: '20px!important',
    width: '100%',
    maxWidth: '300px',
    height: '50px',
    background: '#1a2a5e',
    color: "#FFFFFF",
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.15)',
    borderRadius: '5px',
    border: 'none',
  },
  form: {
    width: '100%',
    maxWidth: '340px',
    margin: 'auto',
    textAlign: 'center',
    background: 'rgba(255, 255, 255, 0.85)',
    borderRadius: '10px',
    padding: '30px 25px',
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)'
  },
  forgotLink: {
    marginTop: '10px',
    color: 'rgba(74, 90, 138, 1)',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontSize: '14px'
  }
}));


// Friendly messages for the ?sso_error=<code> the OIDC/SSO callback redirects with.
const SSO_ERROR_MESSAGES = {
  missing_email: 'Single sign-on is missing an email address.',
  oidc_not_configured: "Single sign-on isn't set up for your organization.",
  sso_unavailable: 'Single sign-on is temporarily unavailable. Please try again.',
  sso_denied: 'Sign-in was cancelled or denied by your identity provider.',
  sso_bad_callback: 'Single sign-on failed. Please try again.',
  sso_failed: "We couldn't verify your single sign-on. Please try again.",
  no_sdp_account: 'No SDP account is linked to that email. Please contact your administrator.',
  account_disabled: 'Your account has been disabled. Please contact your administrator.',
};
const ssoErrorMessage = (code) =>
  SSO_ERROR_MESSAGES[code] || 'Single sign-on failed. Please try again.';

function Login() {
  const mainPanelRef = useRef(null);
  require("../assets/scss/black-dashboard-react.scss");
  const theme = React.useContext(ThemeContext);
  const isDark = true;
  const classes = useStyles();
  let history = useHistory();
  const location = useLocation();

  const { login, isAuthenticated, error: authError } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // If already authenticated, redirect
  React.useEffect(() => {
    if (isAuthenticated) {
      redirectByRole();
    }
  }, [isAuthenticated]);

  // Surface SSO/OIDC failures (callback redirects to /login?sso_error=<code>),
  // then strip the param so it doesn't linger on refresh.
  React.useEffect(() => {
    const ssoError = new URLSearchParams(location.search).get('sso_error');
    if (ssoError) {
      setError(ssoErrorMessage(ssoError));
      history.replace('/login');
    }
  }, [location.search, history]);

  const redirectByRole = async () => {
    const user = await getUser();
    if (!user) return;
    switch (user.role) {
      case roles.superadmin:
        history.push("/companies");
        break;
      case roles.admin:
        history.push("/dashboard");
        break;
      default:
        history.push("/templates");
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await login(email, password);
      // Auth context will update isAuthenticated, which triggers the redirect
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <React.Fragment>
      {loading && <CoreSpinner loading={loading} />}
      <div className={[classes.Wrapper, "wrapper"]}>
        <div ref={mainPanelRef} style={{ position: 'relative', float: 'right', width: '100%', minHeight: '100vh', background: 'linear-gradient(to bottom right, #4a5a8a, #8ba4d9, #c5d5f0)' }}>
          <img className={classes.PBCS} src="/C2_Logo_WhiteBG_cropped.png" alt="Clinical Squared" style={{ width: 180, height: 'auto', objectFit: 'contain' }} />
          <div className={classes.box}>
            <form className={classes.form} onSubmit={handleSubmit}>
              <img src={LogoDark} alt="SDP Logo" style={{ width: 120, marginBottom: 10 }} />
              <p style={{ color: '#1a2a5e', fontSize: 16, fontWeight: 500, marginBottom: 20 }}>Secure Document Publisher</p>
              {(error || authError) &&
                <p style={{ color: 'red', marginBottom: '10px' }}>{error || authError}</p>
              }
              <TextBox
                required
                id="email"
                label="Email Address"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                inputProps={{ maxLength: 50, style: { color: '#333' } }}
                InputLabelProps={{ shrink: true, style: { color: '#555' } }}
              />
              <TextBox
                required
                id="password"
                label="Password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                inputProps={{ maxLength: 50, style: { color: '#333' } }}
                InputLabelProps={{ shrink: true, style: { color: '#555' } }}
              />
              <Button
                type="submit"
                className={classes.LoginButton}
                variant="contained"
                disabled={loading || !email || !password}
              >
                {loading ? 'Signing in...' : 'Log In'}
              </Button>
              <p
                className={classes.forgotLink}
                onClick={() => history.push('/forgot')}
              >
                Forgot Password?
              </p>
            </form>
          </div>
          <div style={{ position: "absolute", bottom: 0, right: 0 }}>
            <Footer />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default Login;
